import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { api, usdToCents } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { MultiImageUpload } from '@/components/ImageUpload';

export default function RequestCreatePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', category: '', budgetUsd: '', photos_base64: [], desired_date: '',
    city: user?.city || '', commune: user?.commune || '', urgent: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/catalog/categories'), api.get('/catalog/cities')]).then(([c, ci]) => {
      setCategories(c.data.items || []); setCities(ci.data.items || []);
    });
  }, []);
  useEffect(() => { const c = cities.find((x) => x.name === form.city); setCommunes(c?.communes || []); }, [form.city, cities]);

  useEffect(() => { if (user && user.role !== 'employeur') { toast.error('Réservé aux employeurs'); nav('/'); } }, [user, nav]);

  const submit = async () => {
    if (!form.title || !form.description || !form.category || !form.city || !form.commune) return toast.error('Champs requis');
    setSaving(true);
    try {
      await api.post('/requests', {
        title: form.title, description: form.description, category: form.category,
        budget_cents: usdToCents(form.budgetUsd),
        photos_base64: form.photos_base64, desired_date: form.desired_date || null,
        city: form.city, commune: form.commune, urgent: form.urgent,
      });
      toast.success('Demande publiée');
      nav('/dashboard');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="request-create-page">
      <h1 className="text-2xl font-bold">Publier une demande</h1>
      <Card className="p-4 space-y-4">
        <div><Label>Titre de la demande</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Fuite dans la cuisine" data-testid="request-title" /></div>
        <div>
          <Label>Catégorie</Label>
          <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="request-category">
            <option value="">Choisir…</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
        <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez précisément votre besoin…" data-testid="request-description" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Ville</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, commune: '' })} data-testid="request-city">
              <option value="">—</option>
              {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Commune</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} data-testid="request-commune">
              <option value="">—</option>
              {communes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Budget indicatif ($)</Label><Input type="number" step="0.01" value={form.budgetUsd} onChange={(e) => setForm({ ...form, budgetUsd: e.target.value })} data-testid="request-budget" /></div>
          <div><Label>Date souhaitée</Label><Input type="date" value={form.desired_date} onChange={(e) => setForm({ ...form, desired_date: e.target.value })} data-testid="request-date" /></div>
        </div>
        <div>
          <Label>Photos (optionnel)</Label>
          <MultiImageUpload values={form.photos_base64} onChange={(v) => setForm({ ...form, photos_base64: v })} max={3} testid="request-photos" />
        </div>
        <div className="flex items-center justify-between bg-[#FFF6C7] border border-[#F7D618]/60 rounded-lg p-3">
          <div>
            <p className="font-semibold text-sm text-[#7A5F00]">Mission urgente (sous 24h)</p>
            <p className="text-xs text-[#5C4600]">Sera mise en avant dans les “Jobs urgents”.</p>
          </div>
          <Switch checked={form.urgent} onCheckedChange={(v) => setForm({ ...form, urgent: v })} data-testid="request-urgent" />
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="request-submit-btn">
          {saving ? 'Publication…' : 'Publier ma demande'}
        </Button>
      </Card>
    </div>
  );
}
